<script setup lang="ts">
import AlertComponent from "@/components/AlertComponent.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import useCostCenter from "@/composables/useCostCenter";
import useDepartmentSearch from "@/composables/useDepartmentSearch";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { CostCenter } from "@/interfaces/costCenter";
import { Department } from "@/interfaces/department";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import useCompany from "@/modules/companies/composables/useCompany";
import useCompanyCostCenter from "@/modules/companies/composables/useCompanyCostCenter";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref, watch } from "vue";
import { Collapse } from "vue-collapsed";
import { useToast } from "vue-toastification";

interface Props extends CompanyEditTab {}

const props = defineProps<Props>();
const headers = ["Clave", "Nombre CECO", "Departamento"];
const mandatoryCostCenter = ref(props.company.mandatoryCostCenter);
const confirmedMandatoryCostCenter = ref(props.company.mandatoryCostCenter);
const creatingCostCenter = ref(false);
const confirmingDelete = ref(false);
const selectedCostCenter = ref<CostCenter | undefined>();
const costCenterToDelete = ref<CostCenter | undefined>();

const {
  createCompanyCostCenterAsync,
  data: costCenters,
  isLoading: costCentersLoading,
} = useCompanyCostCenter({
  companyId: props.company.id,
});

const {
  updateCostCenterAsync,
  updateCostCenterLoading,
  deleteCostCenterAsync,
  deleteCostCenter,
} = useCostCenter({
  params: {},
  immediate: false,
});

const { updateCompanyAsync, updateCompanyLoading } = useCompany();
const toast = useToast();
const costCenter = reactive({
  companyId: props.company.id,
  name: "",
  key: "",
  department: {} as Department,
});

const queryClient = useQueryClient();

const invalidateQueryData = async () => {
  await queryClient.invalidateQueries(["companyCostCenter", props.company.id]);
};

const { hasPermissionUser } = useHasPermissionUser();
const canUpdateCompany = computed(() =>
  hasPermissionUser("update", "App\\Models\\Company"),
);

const rules = {
  key: {
    required: helpers.withMessage("La llave es requerida", required),
  },
  name: {
    required: helpers.withMessage("El nombre es requerido", required),
  },
  department: {
    required: helpers.withMessage("El departamento es requerido", required),
  },
};

const v$ = useVuelidate(rules, costCenter);

const updateCostCenter = ref({
  key: "",
  name: "",
});
const { departments, setQuery } = useDepartmentSearch({
  params: {
    filters: {
      company: props.company.id,
    },
  },
  immediate: true,
});

const checkSelected = (costCenter: CostCenter) => {
  return selectedCostCenter.value?.id === costCenter.id;
};

const resetState = () => {
  costCenter.name = "";
  costCenter.key = "";
  costCenter.department = {} as Department;
};

const handleCreate = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message, {
        timeout: 2500,
      });
      return;
    }
  }

  try {
    await createCompanyCostCenterAsync({
      params: { ...costCenter, departmentId: costCenter.department.id },
      config: {
        raiseToast: true,
        onSuccessToast: "Centro de costos creado con éxito",
        onErrorToast: "Error al crear centro de costos",
      },
    });
    resetState();
    creatingCostCenter.value = false;
    invalidateQueryData();
  } catch (err) {
    console.log(err);
  }
};

const handleSave = async () => {
  try {
    if (
      selectedCostCenter.value &&
      hasPermissionUser("update-costCenters", "App\\Models\\Company")
    ) {
      await updateCostCenterAsync({
        params: {
          id: selectedCostCenter.value?.id,
          companyId: props.company.id,
          ...updateCostCenter.value,
        },
        config: {
          raiseToast: true,
          onSuccessToast: "Centro de costos actualizado con éxito",
          onErrorToast: "Error al actualizar centro de costos",
        },
      });
    }

    if (canUpdateCompany.value) {
      const updatedCompany = await updateCompanyAsync({
        companyId: props.company.id,
        mandatoryCostCenter: mandatoryCostCenter.value,
      });
      if (!updatedCompany) {
        mandatoryCostCenter.value = confirmedMandatoryCostCenter.value;
        return;
      }

      await queryClient.invalidateQueries(["company", props.company.id]);
      confirmedMandatoryCostCenter.value = mandatoryCostCenter.value;
    }
  } catch (err) {
    mandatoryCostCenter.value = confirmedMandatoryCostCenter.value;
    toast.error("No se pudo actualizar la configuración de centro de costos");
  } finally {
    await invalidateQueryData();
    selectedCostCenter.value = undefined;
  }
};

const handleDelete = async (costCenter?: CostCenter) => {
  try {
    if (costCenter) {
      await deleteCostCenterAsync({
        params: {
          id: costCenter.id,
        },
        config: {
          raiseToast: true,
          onSuccessToast: "Centro de costos eliminado con éxito",
          onErrorToast: "Error al eliminar centro de costos",
        },
      });

      invalidateQueryData();
      costCenterToDelete.value = undefined;
    }
  } catch (err) {
    console.log(err);
  } finally {
    confirmingDelete.value = false;
  }
};

const setSelectedCostCenter = (costCenter: CostCenter) => {
  const current = selectedCostCenter.value;

  if (current && current?.id === costCenter.id) {
    selectedCostCenter.value = undefined;
  } else {
    selectedCostCenter.value = costCenter;
  }
};

watch(selectedCostCenter, (value) => {
  if (value) {
    updateCostCenter.value = {
      key: value.key,
      name: value.name,
    };
  }
});

watch(
  () => props.company,
  (value) => {
    mandatoryCostCenter.value = value.mandatoryCostCenter;
    confirmedMandatoryCostCenter.value = value.mandatoryCostCenter;
  },
);
</script>

<template>
  <RoundedBox class="flex-col bg-white">
    <div>
      <div class="flex justify-between mb-[1rem]">
        <h2 class="text-xl font-semibold">Centros de costos</h2>

        <HasPermission
          name-permission="create-costCenters"
          :entity-type="'App\\Models\\Company'"
        >
          <StyledButton
            class="bg-[#253C70] text-white ml-auto flex items-center gap-[0.75rem] !px-2"
            :on-click="() => (creatingCostCenter = !creatingCostCenter)"
          >
            <span class="material-symbols-outlined"> add_circle </span>

            <span class="text-base hidden md:block"
              >Agregar centro de costos</span
            >
          </StyledButton>
        </HasPermission>
      </div>

      <Collapse :when="creatingCostCenter" class="v-collapse">
        <form
          @submit.prevent=""
          class="flex flex-col md:items-end md:flex-row gap-[2.25rem]"
        >
          <FieldSet class="!gap-x-[2.25rem]">
            <FieldSet class="!gap-x-[2.25rem] h-full">
              <StyledInput
                v-model="costCenter.key"
                label="Clave"
                placeholder="ej. 816"
              />

              <StyledInput
                v-model="costCenter.name"
                label="Nombre CECO"
                placeholder="ej. Análisis"
              />
            </FieldSet>

            <StyledCombobox
              v-model="costCenter.department"
              label="Departamento"
              placeholder="Seleccione un departamento"
              :options="departments"
              :display-value="(department) => department.name"
              :on-input-change="setQuery"
            >
              <template #default="{ option }">
                <span>{{ option.name }}</span>
              </template>
            </StyledCombobox>
          </FieldSet>

          <StyledButton
            class="bg-[#253C70] text-white h-fit ml-auto !p-[0.65rem] !py-[0.25rem]"
            @click="handleCreate"
          >
            <span class="material-symbols-outlined text-2xl"> save </span>
            <p>Guardar</p>
          </StyledButton>
        </form>
      </Collapse>
    </div>

    <div v-if="!costCentersLoading" class="grid gap-[1.563rem] mt-[1.563rem]">
      <div class="grid gap-3 overflow-x-auto overflow-y-hidden p-0.5">
        <div class="grid grid-set">
          <h3 v-for="header in headers" class="header" :key="header">
            {{ header }}
          </h3>
        </div>

        <hr />

        <div
          class="grid gap-x-2 items-center grid-set"
          v-for="costCenter in costCenters"
          :key="costCenter.id"
        >
          <template v-if="checkSelected(costCenter)">
            <input
              v-model="updateCostCenter.key"
              type="text"
              class="text-xs ring-1 ring-gray-600 rounded-sm p-0.5"
              @keydown="($event) => $event.key === 'Enter' && handleSave()"
            />

            <input
              v-model="updateCostCenter.name"
              type="text"
              class="text-xs ring-1 ring-gray-600 rounded-sm p-0.5"
              @keydown="($event) => $event.key === 'Enter' && handleSave()"
            />
          </template>

          <template v-else>
            <span>{{ costCenter.key }}</span>
            <span>{{ costCenter.name }}</span>
          </template>

          <span>{{ costCenter.departmentName }}</span>

          <div class="flex items-center justify-end gap-2">
            <HasPermission
              name-permission="update-costCenters"
              :entity-type="'App\\Models\\Company'"
            >
              <button
                class="material-symbols-outlined text-[#2A5C8A]"
                @click="setSelectedCostCenter(costCenter)"
              >
                edit
              </button>
            </HasPermission>

            <HasPermission
              name-permission="delete-costCenters"
              :entity-type="'App\\Models\\Company'"
            >
              <button
                class="material-symbols-outlined text-[#DC2626]"
                @click="
                  () => {
                    costCenterToDelete = costCenter;
                    confirmingDelete = true;
                  }
                "
              >
                delete
              </button>
            </HasPermission>
          </div>
        </div>
      </div>

      <fieldset>
        <label class="flex items-center select-none">
          <input
            v-model="mandatoryCostCenter"
            :checked="mandatoryCostCenter"
            :disabled="!canUpdateCompany || updateCompanyLoading"
            type="checkbox"
            name="mandatoryCostCenter"
          />
          <span class="ml-2 text-base"
            >Solicitar siempre centro de costo al reservar</span
          >
        </label>
      </fieldset>
    </div>

    <div v-else-if="costCenters?.length && costCentersLoading" class="mt-6">
      <p>No hay centros de costos agregados.</p>
    </div>

    <div v-else class="mt-6">
      <p>Cargando centro de costos...</p>
    </div>

    <AlertComponent
      v-model="confirmingDelete"
      :type="'warning'"
      :dismiss="true"
      :on-confirm="() => handleDelete(costCenterToDelete)"
    >
      <template #title>
        {{ "¿Estás seguro de eliminar este centro de costos?" }}
      </template>

      <template #body>
        {{ "Esta acción no se puede deshacer." }}
      </template>

      <template #closeButton> Cancelar </template>

      <template #button> Eliminar </template>
    </AlertComponent>

    <StyledButton
      v-if="
        hasPermissionUser('update', 'App\\Models\\Company') ||
        hasPermissionUser('update-costCenters', 'App\\Models\\Company')
      "
      label="Guardar"
      class="bg-[#253C70] text-white ml-auto mt-[2rem]"
      @click="handleSave"
      :loading="updateCompanyLoading"
      :disabled="updateCompanyLoading"
    />
  </RoundedBox>
  <LoadingModal :when="costCentersLoading" />
</template>

<style scoped>
.grid-set {
  @apply grid-cols-[repeat(4,_minmax(144px,_1fr))];
}
</style>
